import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, RefreshCcw, Search } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChatPanel } from "@/components/ChatPanel";
import {
  PageContainer, PageHeader, StatusPill, Skeleton, relativeTime,
} from "./bdcUi";

const BdcChat = () => {
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState("mine");
  const [q, setQ] = useState("");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  // "+ New Chat" modal
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bdc/threads", { params: { assignment, is_closed: false, q: q || undefined } });
      setThreads(data);
      if (!active && data.length) setActive(data[0]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [assignment, q, active]);

  useEffect(() => { load(); }, [assignment]); // eslint-disable-line react-hooks/exhaustive-deps

  const messagesEndpoint = async (after) => {
    if (!active) return { data: { messages: [] } };
    if (!after) {
      const r = await api.get(`/bdc/threads/${active.id}`);
      return { data: { messages: r.data.messages || [] } };
    }
    return api.get(`/bdc/threads/${active.id}/messages`, { params: { after } });
  };
  const sendEndpoint = (body) => api.post(`/bdc/threads/${active.id}/messages`, { body });

  const handleCreated = async (threadId) => {
    // Refetch list, then select the new thread.
    try {
      const { data } = await api.get("/bdc/threads", { params: { assignment: "mine", is_closed: false } });
      setAssignment("mine");
      setThreads(data);
      const newThread = data.find((t) => t.id === threadId);
      if (newThread) setActive(newThread);
      setNewOpen(false);
      toast.success("Chat started");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <PageContainer className="!py-4">
      <PageHeader
        title="Chat inbox"
        subtitle="All active BDC conversations. Switch to Mine / All to filter."
        testId="bdc-chat-header"
        right={
          <>
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              onClick={() => setNewOpen(true)}
              data-testid="bdc-chat-new-btn"
            >
              <Plus size={15} /> New chat
            </Button>
            <Button variant="outline" onClick={load} data-testid="bdc-chat-refresh"><RefreshCcw size={15} /> Refresh</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[70vh]">
        <div className="rounded-2xl border border-slate-200 bg-white flex flex-col overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex flex-col gap-2">
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="bdc-chat-search" />
            <Tabs value={assignment} onValueChange={setAssignment}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="mine">Mine</TabsTrigger>
                <TabsTrigger value="unclaimed">Unclaimed</TabsTrigger>
                <TabsTrigger value="all_bdc">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 space-y-2">{[0,1,2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : threads.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">No conversations.</p>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${active?.id === t.id ? "bg-brand/5" : ""}`}
                  data-testid={`bdc-chat-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{t.customer_name || t.guest_email || "Guest"}</p>
                    {t.unread_count_bdc > 0 && <StatusPill tone="rose">{t.unread_count_bdc}</StatusPill>}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{t.last_message_preview || "—"}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{relativeTime(t.last_message_at || t.created_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-3 flex flex-col">
          {active ? (
            <>
              <div className="pb-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">{active.customer_name || active.guest_email || "Guest"}</p>
                  <p className="text-xs text-slate-500">{active.vehicle_summary}</p>
                </div>
                <Button asChild variant="ghost" size="sm" className="text-brand">
                  <Link to={`/bdc/leads/${active.lead_id}`}>Open lead →</Link>
                </Button>
              </div>
              <div className="mt-3 flex-1 min-h-0">
                <ChatPanel
                  viewerType="bdc"
                  messagesEndpoint={messagesEndpoint}
                  sendEndpoint={sendEndpoint}
                  testIdPrefix="bdc-chat-active"
                />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              <div className="text-center space-y-3">
                <p>Pick a conversation to open</p>
                <Button variant="outline" onClick={() => setNewOpen(true)} data-testid="bdc-chat-new-empty-btn"><Plus size={14} /> Start a new chat</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <NewChatModal
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={handleCreated}
        onNavigateLead={(leadId) => navigate(`/bdc/leads/${leadId}`)}
      />
    </PageContainer>
  );
};

// ---------------------------------------------------------------------------
// New Chat modal — proactively DMs a customer (existing or new guest) about a
// specific vehicle. Creates a lead + thread server-side via
// POST /api/bdc/chat/new-thread.
// ---------------------------------------------------------------------------

const NewChatModal = ({ open, onOpenChange, onCreated }) => {
  const [tab, setTab] = useState("existing");

  // Existing customer picker
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custLoading, setCustLoading] = useState(false);

  // New guest fields
  const [gName, setGName] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gPhone, setGPhone] = useState("");

  // Vehicle picker
  const [vehQuery, setVehQuery] = useState("");
  const [vehResults, setVehResults] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehLoading, setVehLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset state when modal reopens.
  useEffect(() => {
    if (!open) {
      setTab("existing");
      setCustQuery(""); setCustResults([]); setSelectedCustomer(null);
      setGName(""); setGEmail(""); setGPhone("");
      setVehQuery(""); setVehResults([]); setSelectedVehicle(null);
      setMessage("");
    }
  }, [open]);

  // Debounced customer search
  useEffect(() => {
    if (tab !== "existing") return;
    const t = setTimeout(async () => {
      setCustLoading(true);
      try {
        const { data } = await api.get("/bdc/customers/search", { params: { q: custQuery } });
        setCustResults(data.items || []);
      } catch (e) {
        setCustResults([]);
      } finally { setCustLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery, tab]);

  // Debounced vehicle search (uses public marketplace endpoint)
  useEffect(() => {
    const t = setTimeout(async () => {
      setVehLoading(true);
      try {
        const { data } = await api.get("/vehicles", { params: { q: vehQuery || undefined, page_size: 12 } });
        setVehResults(data.items || []);
      } catch (e) {
        setVehResults([]);
      } finally { setVehLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [vehQuery]);

  const canSubmit = selectedVehicle && (
    (tab === "existing" && selectedCustomer) ||
    (tab === "guest" && gName.trim() && gEmail.trim() && gPhone.trim())
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const body = { vehicle_id: selectedVehicle.id };
    if (tab === "existing") {
      body.customer_id = selectedCustomer.id;
    } else {
      body.guest_name = gName.trim();
      body.guest_email = gEmail.trim();
      body.guest_phone = gPhone.trim();
    }
    if (message.trim()) body.initial_message = message.trim();
    try {
      const { data } = await api.post("/bdc/chat/new-thread", body);
      onCreated?.(data.chat_thread_id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bdc-new-chat-dialog">
        <DialogHeader>
          <DialogTitle>Start a new chat</DialogTitle>
          <DialogDescription>
            Proactively reach out to a customer about a specific vehicle. A lead is created and assigned to you automatically.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full" data-testid="bdc-new-chat-tabs">
            <TabsTrigger value="existing" data-testid="bdc-new-chat-tab-existing">Existing customer</TabsTrigger>
            <TabsTrigger value="guest" data-testid="bdc-new-chat-tab-guest">New guest</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="space-y-4 mt-2">
          {tab === "existing" ? (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Search customers</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Type name, email or phone…"
                  value={custQuery}
                  onChange={(e) => { setCustQuery(e.target.value); setSelectedCustomer(null); }}
                  data-testid="bdc-new-chat-cust-search"
                />
              </div>
              <div className="rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
                {custLoading ? (
                  <p className="text-xs text-slate-400 text-center p-3">Searching…</p>
                ) : custResults.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center p-3">No customers match.</p>
                ) : custResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedCustomer(c)}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${selectedCustomer?.id === c.id ? "bg-brand/5" : ""}`}
                    data-testid={`bdc-new-chat-cust-${c.id}`}
                  >
                    <div className="text-sm font-medium text-slate-800">{c.full_name || "—"}</div>
                    <div className="text-xs text-slate-500">{c.email} · {c.phone || "no phone"}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Full name *</Label>
                <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Jane Doe" data-testid="bdc-new-chat-guest-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email *</Label>
                <Input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="jane@example.com" data-testid="bdc-new-chat-guest-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone *</Label>
                <Input value={gPhone} onChange={(e) => setGPhone(e.target.value)} placeholder="617-555-0100" data-testid="bdc-new-chat-guest-phone" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Vehicle *</Label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search inventory (make/model/year/VIN)…"
                value={vehQuery}
                onChange={(e) => { setVehQuery(e.target.value); setSelectedVehicle(null); }}
                data-testid="bdc-new-chat-veh-search"
              />
            </div>
            <div className="rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
              {vehLoading ? (
                <p className="text-xs text-slate-400 text-center p-3">Searching…</p>
              ) : vehResults.length === 0 ? (
                <p className="text-xs text-slate-400 text-center p-3">No matching inventory.</p>
              ) : vehResults.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setSelectedVehicle(v)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-center gap-3 ${selectedVehicle?.id === v.id ? "bg-brand/5" : ""}`}
                  data-testid={`bdc-new-chat-veh-${v.id}`}
                >
                  <div className="w-14 h-10 rounded-md overflow-hidden bg-slate-100 shrink-0">
                    {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{v.year} {v.make} {v.model} {v.trim || ""}</div>
                    <div className="text-xs text-slate-500">${v.price?.toLocaleString?.() || "—"} · {v.mileage?.toLocaleString?.() || "—"} mi</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Opening message (optional)</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! Saw you were interested in this vehicle — want to schedule a test drive?"
              data-testid="bdc-new-chat-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bdc-new-chat-cancel">Cancel</Button>
          <Button
            className="bg-brand text-white hover:bg-brand-hover"
            onClick={submit}
            disabled={!canSubmit || submitting}
            data-testid="bdc-new-chat-submit"
          >
            {submitting ? "Starting…" : "Start chat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BdcChat;
